<template>
  <router-link
    class="table-link"
    :to="getTableViewRoute()"
    :data-folder-index="folderIndex"
    :data-title="table.displayName"
  >
    <b-card
      class="card-table"
      no-body
      draggable="true"
    >
      <b-card-body class="pb-0">
        <b-card-title title-tag="h5">
          <font-awesome-icon :icon="['fas', table.tableIcon || 'table']" />
          {{ table.displayName }}
        </b-card-title>
        <b-card-text>
          {{ table.description }}
        </b-card-text>
      </b-card-body>
      <b-card-body
        v-if="canChangePermission(table) || canEdit(table) || canDelete(table)"
        class="action-wrapper"
      >
        <div class="d-flex justify-content-end ">
          <b-button
            v-if="canChangePermission(table)"
            :id="`${$options.filters.displayName(folder.folderName)}-${$options.filters.displayName(table.displayName)}-permissions`"
            class="card-table-btn-permissions"
            size="sm"
            variant="outline-secondary"
            :to="{name: 'dataManagementPermissions', params: {itemId: table.entityGuid, type: permissionItemTypes.table}}"
          >
            <font-awesome-icon :icon="['fas', 'key']" />
            Permissions
          </b-button>
          <b-button
            v-if="showEdit && canEdit(table)"
            :id="`${$options.filters.displayName(folder.folderName)}-${$options.filters.displayName(table.displayName)}-edit`"
            size="sm"
            variant="outline-primary"
            class="ml-1 card-table-btn-edit"
            :to="getTableUpdateViewRoute()"
          >
            <font-awesome-icon :icon="['fas', 'pencil-alt']" />
            Edit
          </b-button>

          <b-button
            v-if="showDelete && canDelete(table)"
            :id="`${$options.filters.displayName(folder.folderName)}-${$options.filters.displayName(table.displayName)}-delete`"
            size="sm"
            variant="outline-danger"
            class="card-table-btn-delete ml-1"
            @click="deleteTableCard($event, table)"
          >
            <font-awesome-icon :icon="['fas', 'trash']" />
            Delete
          </b-button>
        </div>
      </b-card-body>
    </b-card>
  </router-link>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';

const {mapState, mapActions, mapMutations} = createNamespacedHelpers('dataManagement');

export default {
  name: 'TableCard',
  props: {
    table: {
      type: Object,
      required: true,
    },
    folder: {
      type: Object,
      required: true,
    },
    module: {
      type: Object,
      required: true,
    },
    folderIndex: {
      type: Number,
    },
    showEdit: {
      type: Boolean,
      default: true,
    },
    showDelete: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      groups: [],
      currentTableData: {},
    };
  },
  computed: {
    ...mapState(['folders', 'permissionTypes']),
    ...mapState({
      permissionItemTypes: state => state.permissionItemTypes,
    }),
    isPowerAppsEntity() {
      const entityConfigJSON = JSON.parse(this.table.entityConfigJSON);
      return entityConfigJSON.EntityType === 'powerApp';
    },
    isPowerBiReportEntity() {
      const entityConfigJSON = JSON.parse(this.table.entityConfigJSON);
      return entityConfigJSON.EntityType === 'powerBiReport';
    },
  },
  methods: {
    ...mapActions(['deleteTable', 'getTableSchema', 'getModuleData', 'getFolders']),
    async deleteTableCard(e, table) {
      e.preventDefault();
      const success = await this.$confirm('Deleting Table...', `You are deleting Table ${table.displayName}. Are you sure you want to continue?`);
      if (success) {
        const { success } = await this.deleteTable(table);
        if (success) {
          this.$toasted.success(`Table "${table.displayName}" was deleted`, {
            duration: TOASTER_DURATION,
          });
        } else {
          this.$toasted.success('Table can not be deleted', {
            duration: TOASTER_DURATION,
          });
        }
      }
      this.getFolders(this.folder.moduleGuid);
    },
    canChangePermission(table) {
      return table.currentUserPermissionLevel === this.permissionTypes.admin;
    },
    canEdit(table) {
      return [this.permissionTypes.admin, this.permissionTypes.edit].includes(table.currentUserPermissionLevel);
      // return table.currentUserPermissionLevel === this.permissionTypes.admin;
    },
    canDelete(table) {
      return table.currentUserPermissionLevel === this.permissionTypes.admin;
    },
    getTableViewRoute() {
      const name = this.isPowerAppsEntity ? 'dataManagementPowerAppsTableView' : (this.isPowerBiReportEntity ? 'dataManagementPowerBiReportView' : 'dataManagementTableView');
      const pathConfig = {
        name,
        params: {
          tableId: this.table.entityGuid,
          moduleUuid: this.module.moduleGuid,
        },
        query: {
          moduleUuid: this.module.moduleGuid,
        },
      };
      const defaultView = this.table.views.find(el => el.isDefault);
      if (defaultView) {
        pathConfig.query.view = defaultView.viewGuid;
      }
      return pathConfig;
    },
    getTableUpdateViewRoute() {
      const name = this.isPowerAppsEntity ? 'dataManagementBuilderPowerApps.update' : (this.isPowerBiReportEntity ? 'dataManagementBuilderPowerBiReport.upsert' : 'dataManagementBuilder.update');
      return { name, params: { tableUuid: this.table.entityGuid }, query: { moduleUuid: this.folder.entityGuid } };
    },
  },
  mounted() {

  },
  destroyed() {
  },
};
</script>

<style lang="scss" scoped>
@import "../../../core/scss/variables";

.card-table {
  position: relative;
  cursor: pointer;
  transition: all 0.3s;
  border-radius: 0;
  height: 100%;
  width: 100%;
  min-height: 10rem;
  min-width: 16rem;

  &:hover {
    box-shadow: 0px 0px 0px 2px $primary;
  }

  .actions-button {
    position: absolute;
    right: 0;
    top: 0;
  }

  .actions-button-wrapper {
    text-align: center;
  }

}

.action-wrapper {
  flex-direction: column;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
}

.table-link {
  text-decoration: none;
  color: unset;
}
</style>
